import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, api } from '../api/client';
import type { Wedding } from '../api/types';
import { Partnersteg } from '../components/Partnerinvitasjon';
import { Alert, Field, LoadingBlock } from '../components/ui';
import { useAuth } from '../context/AuthContext';

/**
 * Onboarding i fem korte steg.
 *
 * Only the couple's names are required. Everything else can be skipped, because
 * an empty field should never be what stops someone from getting started.
 *
 * Stegene med bare valgfrie felt har derfor en egen «Hopp over» ved siden av
 * «Neste». Den tømmer steget før den går videre: «Neste» tar med det som står,
 * «Hopp over» betyr «det tar vi senere» – også om man rakk å begynne på noe.
 *
 * Bryllupet opprettes på vei inn i siste steg, ikke på vei ut. Det siste steget
 * er å invitere den andre i brudeparet, og en invitasjon må ha et bryllup å
 * invitere til. Da er også det som er lagt inn, lagret før man blir bedt om noe
 * som kan hoppes over – hopper man over, er ingenting tapt.
 */

const GUEST_CHOICES = [
  { label: 'Under 50', value: 40, icon: '☕' },
  { label: '50–100', value: 75, icon: '🍽' },
  { label: '100–150', value: 125, icon: '🎉' },
  { label: 'Over 150', value: 180, icon: '🎪' },
];

const BUDGET_CHOICES = [
  { label: 'Under 100 000', value: 80_000 },
  { label: '100–200 000', value: 150_000 },
  { label: '200–350 000', value: 275_000 },
  { label: 'Over 350 000', value: 400_000 },
];

const TOTAL_STEPS = 5;

export function OnboardingPage() {
  const { status, weddings, user, refresh, selectWedding } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  /** Satt så snart bryllupet finnes. Siste steget trenger det å invitere til. */
  const [weddingId, setWeddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    partner1Name: '',
    partner2Name: '',
    weddingDate: '',
    venue: '',
    city: '',
    guestEstimate: null as number | null,
    budgetTotal: null as number | null,
  });

  if (status === 'loading') return <LoadingBlock />;
  if (status === 'anonymous') return <Navigate to="/logg-inn" replace />;
  // Har man et bryllup fra før, hører man ikke hjemme her – men ikke det vi
  // nettopp opprettet i steg fire, for da står vi midt i siste steg.
  if (weddings.length > 0 && weddingId === null) return <Navigate to="/app" replace />;

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const namesFilled = form.partner1Name.trim() !== '' && form.partner2Name.trim() !== '';

  /**
   * Oppretter bryllupet og går videre til invitasjonssteget.
   *
   * Skjemaet sendes inn som argument og leses ikke fra tilstanden: hopper man
   * over steget som oppretter bryllupet, er feltene nettopp tømt, og `form`
   * har ikke rukket å få det med seg ennå.
   */
  const opprett = async (skjema: typeof form) => {
    setBusy(true);
    setError(null);
    try {
      const { wedding } = await api.post<{ wedding: Wedding }>('/weddings', {
        partner1Name: skjema.partner1Name.trim(),
        partner2Name: skjema.partner2Name.trim(),
        weddingDate: skjema.weddingDate || null,
        venue: skjema.venue.trim() || null,
        city: skjema.city.trim() || null,
        guestEstimate: skjema.guestEstimate,
        budgetTotal: skjema.budgetTotal,
      });
      // Settes før `refresh`, som legger bryllupet i konteksten: uten det ville
      // vakten øverst sendt oss rett til /app og hoppet over siste steget.
      setWeddingId(wedding.id);
      setStep(TOTAL_STEPS - 1);
      setBusy(false);
      await refresh();
      selectWedding(wedding.id);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Klarte ikke å opprette bryllupet.',
      );
      setBusy(false);
    }
  };

  /**
   * Hvem av de to som skal inviteres.
   *
   * Den som sitter her, er som regel en av dem. Vi gjetter på den andre, og
   * gjetter vi feil, står navnet i et felt som kan endres.
   */
  const fornavn = user?.name?.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  const denAndre =
    fornavn && form.partner1Name.trim().toLowerCase().startsWith(fornavn)
      ? form.partner2Name.trim()
      : form.partner1Name.trim();

  const steps = [
    {
      count: 'Steg 1 av 5',
      title: `Hei ${user?.name?.split(' ')[0] ?? 'der'}! Hvem skal gifte seg?`,
      lead: 'Navnene brukes på svarlenkene gjestene får.',
      canContinue: namesFilled,
      body: (
        <div className="form-grid">
          <Field label="Den ene">
            <input
              className="input"
              value={form.partner1Name}
              placeholder="Ida"
              autoFocus
              onChange={(event) => update('partner1Name', event.target.value)}
            />
          </Field>
          <Field label="Den andre">
            <input
              className="input"
              value={form.partner2Name}
              placeholder="Marius"
              onChange={(event) => update('partner2Name', event.target.value)}
            />
          </Field>
        </div>
      ),
    },
    {
      count: 'Steg 2 av 5',
      title: 'Når er den store dagen?',
      lead: 'Få en nedtelling til bryllupet og en sjekkliste tilpasset datoen deres.',
      canContinue: true,
      // Verdiene steget tømmes til når det hoppes over. At feltet finnes, er
      // det som gir steget en «Hopp over».
      hoppOver: { weddingDate: '' },
      body: (
        <Field label="Bryllupsdato" hint="Ikke bestemt datoen? Den kan dere legge inn senere.">
          <input
            className="input"
            type="date"
            value={form.weddingDate}
            onChange={(event) => update('weddingDate', event.target.value)}
          />
        </Field>
      ),
    },
    {
      count: 'Steg 3 av 5',
      title: 'Hvor skal dere feire?',
      lead: 'Har dere funnet et sted? Legg det inn her, eller hopp over foreløpig.',
      canContinue: true,
      hoppOver: { venue: '', city: '' },
      body: (
        <div className="form-grid">
          <Field label="Sted eller lokale">
            <input
              className="input"
              value={form.venue}
              placeholder="Låven på Sørbø"
              onChange={(event) => update('venue', event.target.value)}
            />
          </Field>
          <Field label="By eller kommune">
            <input
              className="input"
              value={form.city}
              placeholder="Stavanger"
              onChange={(event) => update('city', event.target.value)}
            />
          </Field>
        </div>
      ),
    },
    {
      count: 'Steg 4 av 5',
      title: 'Hvor stort blir bryllupet?',
      lead: 'Omtrent hvor mange blir dere, og hvilket budsjett ser dere for dere? Et anslag holder.',
      canContinue: true,
      hoppOver: { guestEstimate: null, budgetTotal: null },
      body: (
        <>
          <span className="label" style={{ display: 'block', marginBottom: '0.4rem' }}>
            Omtrent antall gjester
          </span>
          <div className="choice-grid">
            {GUEST_CHOICES.map((choice) => (
              <button
                key={choice.value}
                type="button"
                className={`choice${form.guestEstimate === choice.value ? ' selected' : ''}`}
                onClick={() =>
                  update(
                    'guestEstimate',
                    form.guestEstimate === choice.value ? null : choice.value,
                  )
                }
              >
                <span className="choice-icon" aria-hidden="true">
                  {choice.icon}
                </span>
                {choice.label}
              </button>
            ))}
          </div>

          <span
            className="label"
            style={{ display: 'block', margin: '1.4rem 0 0.4rem' }}
          >
            Omtrentlig budsjett <span className="muted">(valgfritt)</span>
          </span>
          <div className="choice-grid">
            {BUDGET_CHOICES.map((choice) => (
              <button
                key={choice.value}
                type="button"
                className={`choice${form.budgetTotal === choice.value ? ' selected' : ''}`}
                onClick={() =>
                  update(
                    'budgetTotal',
                    form.budgetTotal === choice.value ? null : choice.value,
                  )
                }
              >
                {choice.label}
              </button>
            ))}
          </div>
        </>
      ),
    },
    {
      count: 'Steg 5 av 5',
      // Navnet står allerede i steg én. Å spørre «skal Marius være med?» i
      // stedet for «skal dere være to?» er den samme invitasjonen, men den
      // høres ut som den handler om noen.
      title: denAndre ? `Skal ${denAndre} være med?` : 'Skal dere være to om dette?',
      lead: 'Planlegg sammen og hold oversikten begge to. Inviter partneren din til bryllupet dere har opprettet.',
      canContinue: true,
      // Steget har sine egne knapper: «Send invitasjon» og «Hopp over» hører
      // sammen, og en «Neste» ved siden av dem ville vært en tredje vei ut.
      egneKnapper: true,
      body: weddingId ? (
        <Partnersteg
          weddingId={weddingId}
          navnPaaPartner={denAndre}
          onFerdig={() => navigate('/app?velkommen=1')}
        />
      ) : null,
    },
  ];

  const current = steps[step]!;
  const hoppOver = 'hoppOver' in current ? current.hoppOver : undefined;

  const videre = (skjema: typeof form) => {
    // Nest siste steg oppretter bryllupet; `opprett` går videre selv.
    if (step === TOTAL_STEPS - 2) void opprett(skjema);
    else setStep((value) => value + 1);
  };

  return (
    <div className="onboarding">
      <div className="onboarding-card">
        <div className="steps-bar" aria-hidden="true">
          {steps.map((_, index) => (
            <i key={index} className={index <= step ? 'done' : ''} />
          ))}
        </div>

        <div className="step-count">{current.count}</div>
        <h1>{current.title}</h1>
        <p className="lead">{current.lead}</p>

        <Alert>{error}</Alert>

        {'egneKnapper' in current && current.egneKnapper ? (
          current.body
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!current.canContinue) return;
              videre(form);
            }}
          >
            {current.body}

            <div className="onboarding-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setStep((value) => Math.max(0, value - 1))}
                disabled={step === 0 || busy}
              >
                Tilbake
              </button>
              <div className="onboarding-videre">
                {hoppOver ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => {
                      const tomt = { ...form, ...hoppOver };
                      setForm(tomt);
                      videre(tomt);
                    }}
                  >
                    Hopp over
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={!current.canContinue || busy}
                >
                  {busy ? 'Oppretter …' : 'Neste'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
